import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Style/dashboard.css';
import './Style/header.css';
import './Style/common.css';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';
import CustomConfirmation from '../Common/CustomConfirmation';
import EL_AddDNCNumbers from '../SUPPORT/EL_AddDNCNumbers';

interface DropdownItem {
  value: string;
  label: string;
  [key: string]: any;
}

interface SchoolAPI {
  schoolcode: string;
  name: string;
  status: string;
  maxusers: number;
  maxseats: number;
  category: string;
}

interface FamilyItem {
  value: string;
  label: string;
  name: string;
  status: string;
  validity: string;
  mdsAvail: string;
}

interface Login {
  login: string;
  password: string;
  name: string;
  category: string;
  status: string;
  validity: string;
  mdsAvail: string;
  mdsUsed: string;
  std: string;
  points: string;
  incentives: string;
  howAdded: string;
}

interface LoginItem {
  value: string;
  label: string;
  name: string;
  grade: string;
  points: string;
  incentives: string;
}

interface MDReport {
  reportno: number;
  testcompletiondate: string;
  studyyear: number;
  score: number;
}

interface MenuItem {
  code?: string;
  label: string;
  group?: string;
  danger?: boolean;
  navigation?: string;
}

type MenuMap = Record<string, MenuItem[]>;
type UserType = 'SCH' | 'DLR';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [userType, setUserType] = useState<UserType>('DLR');
  const [isActive, setIsActive] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedSchoolCode, setSelectedSchoolCode] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedFamilyCode, setSelectedFamilyCode] = useState('');
  const [selectedLogin, setSelectedLogin] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedMDReport, setSelectedMDReport] = useState('');
  const [userDetails, setUserDetails] = useState<Login | null>(null);
  const [schoolDetails, setSchoolDetails] = useState<SchoolAPI | null>(null);
  const [familyDetails, setFamilyDetails] = useState<FamilyItem | null>(null);
  const [loginDetails, setLoginDetails] = useState<LoginItem | null>(null);
  const [countries, setCountries] = useState<DropdownItem[]>([]);
  const [schoolCodes, setSchoolCodes] = useState<DropdownItem[]>([]);
  const [admins, setAdmins] = useState<DropdownItem[]>([]);
  const [familyCodes, setFamilyCodes] = useState<FamilyItem[]>([]);
  const [logins, setLogins] = useState<LoginItem[]>([]);
  const [teachers, setTeachers] = useState<DropdownItem[]>([]);
  const [students, setStudents] = useState<DropdownItem[]>([]);
  const [mdReports, setMDReports] = useState<DropdownItem[]>([]);
  const [activeUsers, setActiveUsers] = useState(0);
  const [connections, setConnections] = useState(0);
  const [loggedUsers, setLoggedUsers] = useState(0);
  const [errorLogLink, setErrorLogLink] = useState('');
  const [progVersion, setProgVersion] = useState('v1.9.301');
  const [siteName, setSiteName] = useState('Development');
  const [utcTime, setUtcTime] = useState('');
  const [siteStatus, setSiteStatus] = useState('LIVE');
  const [dropdownResponse, setDropdownResponse] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [alertInfo, setAlertInfo] = useState<{
    show: boolean;
    message: string;
    isSuccess: boolean;
  }>({
    show: false,
    message: '',
    isSuccess: false,
  });

  const [showSalesReport, setShowSalesReport] = useState(false);
  const [showSchoolUsage, setShowSchoolUsage] = useState(false);
  const [showLoginUsage, setShowLoginUsage] = useState(false);
  const [showAddDNC, setShowAddDNC] = useState(false);
  const [showSearchDNC, setShowSearchDNC] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState<{
    show: boolean;
    message: string;
    onConfirm?: () => void;
  }>({
    show: false,
    message: '',
    onConfirm: undefined,
  });

  const showAlert = (message: string, isSuccess = false) => {
    setAlertInfo({ show: true, message, isSuccess });
  };

  const openBlockedWindow = (url: string, name: string, features: string) => {
    const newWindow = window.open(url, name, features);
    if (!newWindow) {
      showAlert('Popup blocked. Please allow popups for this site.', false);
      return;
    }
    const timer = window.setInterval(() => {
      if (newWindow.closed) {
        clearInterval(timer);
      }
    }, 500);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);
  const API_BASE_URL = 'https://staging.sysadmin.elearnoncloud.com/';

  const superAdminUsers = ['shubhanga', 'shashanka', 'WILLIAM'];
  const isSuperAdminUser = superAdminUsers.includes(
    (sessionStorage.getItem('L') || '').trim().toLowerCase()
  );

  const menusWithDropdown = ['Admin', 'Data', 'Support', 'Reports', 'SuperAdmin', 'Analysis'];

  const menus: MenuMap = {
    Admin: [
      { code: '1', label: 'Set Site Status' },
      { code: '2', label: 'Manage Site Message', navigation: '/WEBADMIN/ADMIN/EL_SetSiteMessage' },
      { code: '3', label: 'Clear Idle Seats' },
      { code: '4', label: 'Manage Program Updates', navigation: '/WEBADMIN/ADMIN/EL_ShowUpdates' },
      { code: '5', label: 'Send eMail', navigation: '/WEBADMIN/MAILS/EL_SendMail' },
      { label: 'School/eDealer', danger: true },
      { code: '9', label: 'Display List', navigation: '/WebAdmin/SCHOOLS/EL_ListSchools' },
      { code: '10', label: 'Add New', navigation: '/WebAdmin/SCHOOLS/EL_AddNewSchoolCode?CalledFrom=S' },
      { label: 'School', danger: true },
      { code: '11', label: 'Promote Students to Next year', navigation: '/SCHOOL/EL_PromoteSchoolStudentsToNextYear' },
      { label: 'eDealer', danger: true },
      { code: '12', label: 'Add Distributor', navigation: '/DEALER/EL_RegisterDistributors?ARD=A' },
      { code: '13', label: 'Modify Distributor', navigation: '/DEALER/EL_RegisterDistributors?ARD=R' },
      { code: '14', label: 'Login Roles', navigation: '/WEBADMIN/ADMIN/EL_ManageRoles' },
      { label: 'Families', danger: true },
      { code: '15', label: 'Promote Students to Next year', navigation: '/SUPPORT/EL_Promote_Family_Student_Grade' },
    ],
    Data: [
      { label: 'DICS', danger: true },
      { code: '16', label: 'DIC Files', navigation: '/WebAdmin/DIC/EL_ManageDIC' },
      { code: '17', label: 'Keywords', navigation: '/WebAdmin/DIC/EL_ManageKeyWords' },
      { label: 'Product', danger: true },
      { code: '18', label: 'Products', navigation: '/WebAdmin/PRODUCT/EL_ManageProducts' },
      { code: '19', label: 'Packages', navigation: '/WebAdmin/PRODUCT/EL_ManagePackages' },
      { code: '20', label: 'Promotions', navigation: '/WebAdmin/PRODUCT/EL_ManagePromotions' },
      { label: 'Messages', danger: true },
      { code: '21', label: 'Weekly Tips', navigation: '/WebAdmin/LESSONPLAN/EL_ManageWeeklyTips' },
      { code: '22', label: 'WLP Messages', navigation: '/WEBADMIN/LESSONPLAN/EL_LessonPlanMessages' },
      { label: 'Rewards', danger: true },
      { code: '23', label: 'Avatars', navigation: '/WebAdmin/POINTS/EL_ManageAvatars' },
      { code: '24', label: 'Comps', navigation: '/WebAdmin/POINTS/EL_ManageComps' },
      { code: '25', label: 'Stores', navigation: '/WebAdmin/POINTS/EL_ManageStores' },
      { label: 'Maths Doctor', danger: true },
      { code: '31.1', label: 'RESET Maths Doctor', navigation: '/SYSADMIN/MathsDoctor/ResetMD' },
      { code: '26', label: 'Strand/Substrand/Qns', navigation: '/WebAdmin/MATHSDOCTOR/MD_ManageTopics' },
      { code: '27', label: 'Voucher Code', navigation: '/WebAdmin/MATHSDOCTOR/MD_ManageVoucherCodes' },
      { code: '28', label: 'Maths Doctor- Maths Wiz Link', navigation: '/WebAdmin/MATHSDOCTOR/EL_LinkMDandMW' },
      { code: '29', label: 'View Questions', navigation: '/SYSADMIN/MathsDoctor/MathsDoctorQuestions' },
      { code: '29.1', label: 'Test FTP' },
      { code: '30', label: 'View Exceptions', navigation: '/WebAdmin/MATHSDOCTOR/MD_ExceptionError' },
      { code: '31', label: 'Post Question Category' },
      { label: 'Others', danger: true },
      { code: '32', label: 'View Lesson', navigation: '/SUPPORT/EL_ViewLesson' },
      { code: '33', label: 'View Games', navigation: '/WebAdmin/GAMES/EL_ManageGames' },
      { code: '34', label: 'Manage States', navigation: '/WebAdmin/Admin/EL_ManageStates' },
    ],
    Support: [
      { code: '35', label: 'Entry', navigation: '/SUPPORT/EL_ListOfTasks?FilterType=Entry' },
      { code: '36', label: 'View', navigation: '/SUPPORT/EL_ListOfTasks?FilterType=View' },
      { code: '37', label: 'Search Engine - AUS' },
      { code: '38', label: 'Courtesy Call List' },
      { code: '39', label: 'Customer Support Entry Details', navigation: '/SUPPORT/EL_CustomerSupportEntryDetails' },
      { code: '40', label: 'Flags And Alerts', navigation: '/SUPPORT/EL_FlagedAlertList' },
      { label: 'WLP', danger: true },
      { code: '41', label: 'Create MathsDoctor LessonPlan' },
      { code: '42', label: 'Create Default LessonPlan - Maths' },
      { code: '43', label: 'Create Default LessonPlan - English' },
      { code: '44', label: 'View LessonPlan - Maths' },
      { code: '45', label: 'View LessonPlan - English' },
      { code: '46', label: 'Default WLP running out - Maths', navigation: `/SUPPORT/EL_ListWLPsRunningout?Subject=Mathematics&SchoolCode=${sessionStorage.getItem('SelectedSchoolCode')}` },
      { code: '47', label: 'Default WLP running out - English', navigation: `/SUPPORT/EL_ListWLPsRunningout?Subject=English&SchoolCode=${sessionStorage.getItem('SelectedSchoolCode')}` },
      { code: '48', label: 'MathsDoctor WLP running out', navigation: '/SUPPORT/EL_ListWLPsRunningout' },
      { code: '68', label: 'Remove lessons from WLP', navigation: '/SUPPORT/EL_RemoveLessonsFromWLP' },
      { code: '69', label: 'MD started & Not completed', navigation: '/SUPPORT/EL_ListofUsersMDNotCompleted' },
      { code: '70', label: 'Families Registered By APP', navigation: '/WEBADMIN/REPORTS/EL_FamilyRegisteredFromAPP' },
      { label: 'DNC', danger: true },
      { code: '49', label: 'Add DNC' },
      { code: '50', label: 'Search DNC' },
      { label: 'Others', danger: true },
      // C=DLR_KE_AU&F=kedemo&L=Child10
      { code: '51', label: 'View Points', navigation: `/SYSADMIN/Support/ViewPoints?C=${sessionStorage.getItem('SelectedSchoolCode')}&F=${sessionStorage.getItem('SelectedFamilyCode')}&L=${sessionStorage.getItem('SelectedLogin')}` },
      { code: '52', label: 'Show eMails sent to Family' },
      { code: '53', label: 'Write Reports', navigation: '/SUPPORT/TeacherReport/EL_ListFamilies' },
    ],
    Reports: [
      { code: '54', label: 'Structured Reports', navigation: '/WebAdmin/Reports/EL_GeneralQuery' },
      { code: '55', label: 'Active Users Chart' },
      { code: '56', label: 'Tutor Center Usage', navigation: '/WebAdmin/Reports/EL_TutorCenterUsage' },
      { code: '57', label: 'School Usage', navigation: '/WebAdmin/Reports/EL_SchoolUsage' },
      { code: '58', label: 'Family Usage Analysis', navigation: '/WebAdmin/Reports/EL_FamilyUsageAnalysis' },
      { code: '59', label: 'Sales Report' },
      { code: '60', label: 'Login Usage' },
      { label: 'Tutors Report', danger: true },
      { code: '76', label: 'Add/Remove Tutors', navigation: '/SYSADMIN/Reports/AddTutor' },
      { code: '71', label: 'Tutor Entries Report', navigation: '/SYSADMIN/Reports/TutorSupportReport' },
      { code: '73', label: 'Call And Support', navigation: '/SYSADMIN/Reports/CallAndSupport' },
      { code: '74', label: 'CPC Installers Report', navigation: '/SYSADMIN/Reports/CPCInstallersReport' },
      { code: '78', label: 'CS-No Open Tasks', navigation: '/SYSADMIN/Reports/NoOpenTasks' },
      { label: 'Students Work', danger: true },
      { code: '61', label: 'User Diary' },
      { code: '61', label: 'Extension Student', navigation: '/SYSADMIN/Reports/ExtensionStudent' },
      { code: '62', label: 'PT Report' },
      { label: 'Students MD', danger: true },
      { code: '164', label: 'Knowledge Gap Report' },
      { code: '65', label: 'Navigation Report' },
      { code: '66', label: 'Current WLP Report' },
      { code: '67', label: 'Performance Report', navigation: '/MD_Reports/MD_WeeklyPerformanceReport' },
      { code: '68', label: 'New MD PDF Report' },
      { code: '69', label: 'Still Report' },
      { code: '70', label: 'Moving Report' },
    ],
    SuperAdmin: [
      { code: '501', label: 'SysAdmin Users List', navigation: '/WebAdmin/ADMIN/EL_ListUsers' },
      { code: '502', label: 'Link to Payment Website', navigation: '/PAYPAL/EL_SubscriptionStep1' },
      { code: '503', label: 'Manage Database Tables', navigation: '/WebAdmin/ADMIN/EL_GeneralARD' },
      { code: '504', label: 'Manage Settings', navigation: '/WEBADMIN/ADMIN/EL_ManageSettings' },
      { code: '505', label: 'Show Error LOG', navigation: '/WebAdmin/Reports/EL_ShowErrorLog' },
    ],
    Analysis: [
      { code: '81', label: 'Lesson Browser', navigation: '/LessonBrowser/LessonBrowser' },
      { code: '82', label: 'Question Performance', navigation: '/SUPPORT/EL_Questionperformance' },
      { code: '83', label: 'Lesson Performance', navigation: '/SUPPORT/EL_Lessonperformance' },
      { code: '84', label: 'Test Performance', navigation: '/SUPPORT/EL_Testperformance' },
      { code: '85', label: 'Test Summary', navigation: '/SUPPORT/EL_TestSummary' },
    ],
  };

  const groupMenuItemsBySection = (menuItems: MenuItem[]) => {
    const sections: Record<string, MenuItem[]> = {};
    let currentSection = 'Default';

    menuItems.forEach((item) => {
      if (item.danger) {
        currentSection = item.label;
        if (!sections[currentSection]) {
          sections[currentSection] = [];
        }
      } else {
        if (!sections[currentSection]) {
          sections[currentSection] = [];
        }
        sections[currentSection].push(item);
      }
    });

    return sections;
  };

  const sectionHasVisibleItems = (sectionItems: MenuItem[]) => {
    return sectionItems.some((item) => canShowMenuItem(item.code));
  };

  const canShowMenuItem = (code?: string) => {
    if (!code) return true;
    const numCode = parseInt(code, 10);
    if (isNaN(numCode)) return true;
    if (isSuperAdminUser) return true;
    const menuRights = sessionStorage.getItem('MENU_RIGHTS') || '';
    const flag = menuRights.charAt(numCode - 1);
    return flag === 'Y';
  };

  const handleNavigation = (item: MenuItem) => {
    if (item.danger) return;

    if (item.label === 'Sales Report') {
      setShowSalesReport(true);
      return;
    }

    if (item.label === 'School Usage') {
      setShowSchoolUsage(true);
      return;
    }

    if (item.label === 'Login Usage') {
      if (!selectedSchoolCode) {
        setAlertInfo({ show: true, message: "Please select School Code", isSuccess: false });
        return;
      }
      setShowLoginUsage(true);
      return;
    }

    if (item.label === 'Promotions') {
      if (!selectedSchoolCode) {
        setAlertInfo({ show: true, message: "Please select a school code", isSuccess: false });
        return;
      }
      navigate(`/WebAdmin/PRODUCT/EL_ManagePromotions?SchoolCode=${encodeURIComponent(selectedSchoolCode)}`);
      return;
    }

    if (item.label === 'View Points') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      navigate(
        `/SYSADMIN/Support/ViewPoints?C=${encodeURIComponent(selectedSchoolCode)}&F=${encodeURIComponent(selectedFamilyCode)}&L=${encodeURIComponent(selectedLogin)}`
      );
      return;
    }
    if (item.label === 'RESET Maths Doctor') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      navigate(
        `/SYSADMIN/MathsDoctor/ResetMD?SchoolCode=${encodeURIComponent(selectedSchoolCode)}&FamilyCode=${encodeURIComponent(selectedFamilyCode)}&Login=${encodeURIComponent(selectedLogin)}`
      );
      return;
    }
    if (item.label === 'Create MathsDoctor LessonPlan') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      return;
    }
    if (item.label === 'Create Default LessonPlan - Maths') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      return;
    }
    if (item.label === 'Create Default LessonPlan - English') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      return;
    }
    if (item.label === 'View LessonPlan - Maths') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      return;
    }
    if (item.label === 'View LessonPlan - English') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      return;
    }
    if (item.label === 'Default WLP running out - Maths') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      navigate(
        `/SUPPORT/EL_ListWLPsRunningout?Subject=Mathematics&SchoolCode=${encodeURIComponent(selectedSchoolCode)}&FamilyCode=${encodeURIComponent(selectedFamilyCode)}&Login=${encodeURIComponent(selectedLogin)}`
      );
      return;
    }
    if (item.label === 'Default WLP running out - English') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required user", false);
        return;
      }
      navigate(
        `/SUPPORT/EL_ListWLPsRunningout?Subject=English&SchoolCode=${encodeURIComponent(selectedSchoolCode)}&FamilyCode=${encodeURIComponent(selectedFamilyCode)}&Login=${encodeURIComponent(selectedLogin)}`
      );
      return;
    }
    if (item.label === 'User Diary') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please select user.", false);
        return;
      }

      navigate(
        `/SYSADMIN/UserDiary?SchoolCode=${encodeURIComponent(selectedSchoolCode)}&FamilyCode=${encodeURIComponent(selectedFamilyCode)}&Login=A`
      );
      return;
    }
    if (item.label === 'PT Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please select user.", false);
        return;
      }
      navigate(
        `/STUDENT/Reports/EL_PTReport?SchoolCode=${encodeURIComponent(selectedSchoolCode)}&FamilyCode=${encodeURIComponent(selectedFamilyCode)}&Login=A`
      );
      return;
    }
    if (item.label === 'Knowledge Gap Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      return;
    }
    if (item.label === 'Navigation Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      return;
    }
    if (item.label === 'Current WLP Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      return;
    }
    if (item.label === 'Performance Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      return;
    }
    if (item.label === 'New MD PDF Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      return;
    }
    if (item.label === 'Still Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      return;
    }
    if (item.label === 'Moving Report') {
      if (!selectedSchoolCode || !selectedFamilyCode || !selectedLogin) {
        showAlert("Please Select required Student", false);
        return;
      }
      navigate(`/?SchoolCode=${encodeURIComponent(selectedSchoolCode)}&FamilyCode=${encodeURIComponent(selectedFamilyCode)}&Login=${encodeURIComponent(selectedLogin)}`);
      return;
    }

    if (item.label === 'Courtesy Call List') {
      const sCode =
        selectedSchoolCode ||
        sessionStorage.getItem('SelectedSchoolCode') ||
        sessionStorage.getItem('S') ||
        'A';
      const fCode =
        selectedFamilyCode ||
        sessionStorage.getItem('SelectedFamilyCode') ||
        sessionStorage.getItem('F') ||
        'AA';
      const userLogin =
        selectedLogin ||
        sessionStorage.getItem('SelectedLogin') ||
        sessionStorage.getItem('L') ||
        '';

      const url = `/SUPPORT/EL_CourtesyCallList?FilterType=CSCourtesyCall&S=${encodeURIComponent(
        sCode
      )}&F=${encodeURIComponent(fCode)}&L=${encodeURIComponent(userLogin)}`;
      navigate(url);
      return;
    }

    if (item.label === 'Add DNC') {
      setShowAddDNC(true);
      return;
    }

    if (item.label === 'Search DNC') {
      setShowSearchDNC(true);
      return;
    }

    if (item.label === 'Extension Student') {
      const width = 1050;
      const height = 850;
      const left = (window.screen.availWidth - width) / 2;
      const top = (window.screen.availHeight - height) / 2;

      const url = `${item.navigation}?CalledFrom=SysAdmin`;

      const newWindow = window.open(
        url,
        'ExtensionStudent',
        `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,status=yes`
      );

      if (newWindow) {
        const timer = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(timer);
          }
        }, 500);
      }
      return;
    }

    if (item.label === 'Manage States') {
      const width = 600;
      const height = 400;
      const left = (window.screen.availWidth - width) / 2;
      const top = (window.screen.availHeight - height) / 2;

      const url = item.navigation || '/WebAdmin/Admin/EL_ManageStates';

      openBlockedWindow(
        url,
        'ManageStates',
        `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,status=yes`
      );
      return;
    }

    if (item.navigation) {
      navigate(item.navigation);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveTab(null);
      }
    };

    if (activeTab) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeTab]);

  const fetchCountries = async () => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_countries/`);
      const data = await response.json();

      const countriesData = data.countries.map((item: string) => ({
        value: item,
        label: item,
      }));

      if (countriesData.length > 0) {
        setCountries(countriesData);
        setSelectedCountry(countriesData[0].value);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchSchoolCodes = async (country: string, category: UserType = userType) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_schooldcodes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ country, category }),
      });
      const data = await response.json();
      const schoolCodesData = data.map((item: SchoolAPI) => ({
        value: item.schoolcode,
        label: item.schoolcode,
      }));
      setSchoolCodes(schoolCodesData);
      if (schoolCodesData.length > 0) {
        setSelectedSchoolCode(schoolCodesData[0].value);
        fetchSchoolDetails(schoolCodesData[0].value);
        fetchAdmins(schoolCodesData[0].value);
        fetchFamilies(schoolCodesData[0].value);
        fetchTeachers(schoolCodesData[0].value);
        fetchStudents(schoolCodesData[0].value);
      }
    } catch (error) {
      console.error('Error fetching school codes:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchSchoolDetails = async (schoolCode: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_schooldcodes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ country: selectedCountry, category: userType }),
      });
      const data = await response.json();
      const school = data.find((item: SchoolAPI) => item.schoolcode === schoolCode);
      if (school) setSchoolDetails(school);
    } catch (error) {
      console.error('Error fetching school details:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const getUserCategory = () => {
    if (!selectedLogin) return '';
    if (admins.some(admin => admin.value === selectedLogin)) {
      return 'Admin';
    }
    if (familyCodes.some(family => family.value === selectedFamilyCode)) {
      return 'Family User';
    }
    if (teachers.some(teacher => teacher.value === selectedLogin)) {
      return 'Teacher';
    }
    if (students.some(student => student.value === selectedLogin)) {
      return userType === 'SCH' ? 'Student' : 'User';
    }
    return '';
  };

  const fetchAdmins = async (schoolCode: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_admins/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schoolcode: schoolCode }),
      });
      const data: Login[] = await response.json();
      const adminsData = data.map((admin) => ({
        value: admin.login,
        label: admin.login,
      }));
      setAdmins(adminsData);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchFamilies = async (schoolCode: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_families/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schoolcode: schoolCode }),
      });
      const data: Login[] = await response.json();
      const familiesData: FamilyItem[] = data.map((family) => ({
        value: family.login,
        label: family.login,
        name: family.name,
        status: family.status,
        validity: family.validity,
        mdsAvail: family.mdsAvail,
      }));
      setFamilyCodes(familiesData);
    } catch (error) {
      console.error('Error fetching families:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchLogins = async (schoolCode: string, familyCode: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_children/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schoolcode: schoolCode, familycode: familyCode }),
      });
      const data: Login[] = await response.json();
      const loginsData: LoginItem[] = data.map((login) => ({
        value: login.login,
        label: login.name || login.login,
        name: login.name || "",
        grade: login.std,
        points: login.points,
        incentives: login.incentives
      }));
      setLogins(loginsData);
    } catch (error) {
      console.error('Error fetching logins:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchTeachers = async (schoolCode: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_teachers/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schoolcode: schoolCode }),
      });
      const data: Login[] = await response.json();
      const teachersData = data.map((teacher) => ({
        value: teacher.login,
        label: teacher.login,
      }));
      setTeachers(teachersData);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchStudents = async (schoolCode: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_students/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schoolcode: schoolCode, category: userType }),
      });
      const data: Login[] = await response.json();
      const studentsData = data.map((student) => ({
        value: student.login,
        label: student.login,
      }));
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchMDReports = async (schoolCode: string, familyCode: string, login: string) => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/get_md_reports/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schoolcode: schoolCode, familycode: familyCode, login }),
      });
      const data: MDReport[] = await response.json();
      const mdReportsData = data.map((report) => ({
        value: report.reportno.toString(),
        label: `${report.testcompletiondate}`,
      }));
      setMDReports(mdReportsData);
    } catch (error) {
      console.error('Error fetching MD reports:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const fetchActiveUserCount = async () => {
    setDropdownResponse(true);
    try {
      const response = await fetch(`${API_BASE_URL}get_activeusercount/`);
      const data = await response.json();
      setActiveUsers(data.activeusersall || 0);
      setConnections(data.activeconnections || 0);
      setLoggedUsers(data.usedbycount || 0);
    } catch (error) {
      console.error('Error fetching active user count:', error);
    } finally {
      setDropdownResponse(false);
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCountry(value);
    fetchSchoolCodes(value);
  };

  const handleSchoolCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedSchoolCode(value);
    fetchSchoolDetails(value);
    fetchAdmins(value);
    fetchFamilies(value);
    fetchTeachers(value);
    fetchStudents(value);
  };

  const handleFamilyCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedFamilyCode(value);
    const selectedFamily = familyCodes.find(f => f.value === value);
    if (selectedFamily) {
      setFamilyDetails(selectedFamily);
    }
    fetchLogins(selectedSchoolCode, value);
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedLogin(value);
    const selectedLoginObj = logins.find(l => l.value === value);
    if (selectedLoginObj) {
      setLoginDetails(selectedLoginObj);
    }
    fetchMDReports(selectedSchoolCode, selectedFamilyCode, value);
  };

  const handleEdit = () => {
    if (!selectedLogin) {
      alert('Please select a login to edit.');
      return;
    }
    navigate('/SYSADMIN/EditLogin');
  };

  const handleGo = () => {
    if (!selectedLogin) {
      alert('Please select a login.');
      return;
    }
    navigate('https://staging.elearnoncloud.com/LOGIN/SelectUser.aspx');
  };

  const handleViewLesson = () => {
    if (!selectedLogin) {
      alert('Please select a login to view lesson.');
      return;
    }
    navigate('/SUPPORT/EL_ViewLesson');
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
  };

  const toggleDropdown = (tab: string) => {
    setActiveTab(activeTab === tab ? null : tab);
  };

  const renderDropdown = (
    items: DropdownItem[],
    value: string,
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
    label: string,
    id: string,
    index: number,
    options?: {
      showEditLink?: boolean;
      count?: number;
    }
  ) => {
    const backgroundColor = index % 2 === 0 ? 'linen' : 'skyblue';

    return (
      <div className="divTableRow">
        <div
          className="divTableCell"
          style={{ backgroundColor, width: '100px', fontSize: '12px' }}
        >
          <label htmlFor={id}>{label}:</label>
        </div>

        <div
          className="divTableCell"
          style={{ backgroundColor, width: '200px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <select
              id={id}
              value={value}
              onChange={onChange}
              style={{ width: '75%' }}
              disabled={dropdownResponse}
            >
              <option value="">-- Select --</option>
              {items.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            {options?.showEditLink && value && (
              <a
                href="#"
                style={{
                  marginLeft: '6px',
                  fontSize: '13px',
                  color: 'blue',
                  whiteSpace: 'nowrap',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/SYSADMIN/Admin/SchoolDetails');
                }}
              >
                Edit
              </a>
            )}

            {options?.count !== undefined && (
              <span
                style={{
                  marginLeft: '8px',
                  color: 'blue',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {options.count}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    document.body.style.backgroundColor = '#214461';
    fetchCountries();
    fetchActiveUserCount();
    const timer = setInterval(() => {
      setUtcTime(new Date().toUTCString());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchSchoolCodes(selectedCountry, userType);
      sessionStorage.setItem("category", userType);
    }
  }, [selectedCountry, userType]);

  return (
    <div>
      {dropdownResponse && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <Loading />
        </div>
      )}

      <form id="form1">
        <div id="header" style={{ background: 'white', height: '40px' }} ref={dropdownRef}>
          <div className="navbar navbar-default navbar-fixed-top" role="navigation" style={{ marginBottom: '20px' }}>
            <div className="container-fluid">
              <div className="collapse navbar-collapse" id="navbar-collapse" style={{ listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-around', height: '40px' }}>
                <div className="navbar-header">
                  <a className="navbar-brand" href="/dashboard" style={{ color: '#777', fontSize: '18px', padding: '15px', textDecorationLine: 'none' }}>System Administrator</a>
                </div>
                <div>
                  <ul className="nav navbar-nav navbar-right" style={{ listStyle: 'none', display: 'flex', alignItems: 'center' }}>
                    {['Admin', 'Data', 'Support', 'Reports', ...(isSuperAdminUser ? ['SuperAdmin'] : []), 'Analysis']
                      .filter((menu) => {
                        if (menu === 'LessonBrowser') return true;
                        return Object.values(groupMenuItemsBySection(menus[menu])).some((sectionItems) =>
                          sectionHasVisibleItems(sectionItems)
                        );
                      })
                      .map((menu) => {
                        const hasDropdown = menusWithDropdown.includes(menu) &&
                          Object.values(groupMenuItemsBySection(menus[menu])).some((sectionItems) =>
                            sectionHasVisibleItems(sectionItems)
                          );

                        return (
                          <li key={menu} className="dropdown" style={{ position: 'relative', display: 'inline-block' }}>
                            <a
                              href="#"
                              className={`dropdown-toggle ${activeTab === menu ? 'active-menu-item' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveTab(activeTab === menu ? null : menu);
                              }}
                              style={{ display: 'block', padding: '15px', color: '#777', textDecoration: 'none', cursor: 'pointer', fontSize: '14px' }}
                            >
                              {menu.replace('SuperAdmin', 'Super Admin')} <span className="caret"></span>
                            </a>
                            {activeTab === menu && hasDropdown && (
                              <ul className="dropdown-menu" style={{ fontSize: '14px', lineHeight: '16px', position: 'absolute', top: '100%', left: 0, zIndex: 1000, display: 'block', minWidth: '220px', padding: '5px 0', margin: '1px 0 0', backgroundColor: '#fff', border: '1px solid rgba(0,0,0,.15)', borderRadius: '4px', boxShadow: '0 6px 12px rgba(0,0,0,.175)', listStyle: 'none' }}>
                                {Object.entries(groupMenuItemsBySection(menus[menu])).map(([sectionLabel, sectionItems]) => {
                                  if (!sectionHasVisibleItems(sectionItems)) {
                                    return null;
                                  }

                                  return (
                                    <React.Fragment key={sectionLabel}>
                                      {sectionLabel !== 'Default' && (
                                        <li className="dropdown-header" style={{ padding: '3px 20px', fontSize: '12px', color: '#999', fontWeight: 'bold' }}>
                                          {sectionLabel}
                                        </li>
                                      )}
                                      {sectionItems.map((childItem, childIdx) => {
                                        if (canShowMenuItem(childItem.code)) {
                                          return (
                                            <li key={childIdx}>
                                              <a
                                                href="#"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleNavigation(childItem);
                                                }}
                                                style={{ display: 'block', padding: '3px 20px', fontSize: '13px', textDecoration: 'none' }}
                                              >
                                                {childItem.label}
                                              </a>
                                            </li>
                                          );
                                        }
                                        return null;
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    <li style={{ display: 'inline-block' }}>
                      <button
                        onClick={handleLogout}
                        style={{
                          display: 'block',
                          padding: '6px 12px',
                          margin: '8px 4px',
                          backgroundColor: '#ff6600',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div id="user_banner">
        <div id="user_strip_weekly_progress_container">
          <div id="user_strip_weekly_progress_text">
            Now: <span>Active Users: <a href="/active-users">{activeUsers}</a></span>
            <span>Connections: {connections}</span>
          </div>
        </div>
        <div id="user_strip_weekly_achievements_container">
          <div id="user_strip_weekly_achievements_text">
            TODAY: <span>Error Log: <a href="/error-log" target="_blank">View</a></span>
            <span>Total Logged Users: {loggedUsers}</span>
          </div>
        </div>
      </div>

      <div id="math_banner" className="subject_banner" style={{ color: 'white', padding: '10px 5px' }}>
        <span className="bar_titles">
          DEVELOPMENT&nbsp;&nbsp;&nbsp;&nbsp;Version&nbsp;&nbsp;{progVersion}
        </span>
        <p id="lblSiteName">{siteName}</p>
        <span id="lblUTCTime">
          {new Intl.DateTimeFormat('en-GB', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'UTC',
          })
            .format(new Date())
            .replace(',', '') + ' UTC'}
        </span>
        <span id="SiteStatus">Site Status:</span>
        <span style={{ backgroundColor: '#ff3300', color: 'white' }}>&nbsp;&nbsp;&nbsp;&nbsp;{siteStatus}&nbsp;&nbsp;&nbsp;&nbsp;</span>
      </div>

      <div id="english_container" className="subject_container">
        <div style={{ width: '350px', marginLeft: '5%', marginRight: 'auto', marginTop: '25px' }}>
          <div>
            <label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={() => setIsActive(!isActive)}
              />
              Active
            </label>
          </div>
          <div>
            <label>
              <input
                type="radio"
                name="userType"
                checked={userType === 'SCH'}
                onChange={() => setUserType('SCH')}
              />
              School
            </label>
            <label>
              <input
                type="radio"
                name="userType"
                checked={userType === 'DLR'}
                onChange={() => setUserType('DLR')}
              />
              eDealer
            </label>
          </div>
        </div>

        <div className="divTable" style={{ width: '370px', marginLeft: '5%', marginRight: 'auto', fontSize: '10px', color: 'black' }}>
          {[
            { items: countries, value: selectedCountry, onChange: handleCountryChange, label: 'Country', id: 'ddlAllCountries', index: 0 },
            { items: schoolCodes, value: selectedSchoolCode, onChange: handleSchoolCodeChange, label: 'School Code', id: 'ddlAllSchoolCodes', index: 1, options: { showEditLink: true } },
            { items: admins, value: selectedAdmin, onChange: (e: { target: { value: React.SetStateAction<string>; }; }) => setSelectedAdmin(e.target.value), label: 'Admin / Staff', id: 'ddlAdmin', index: 2, options: { count: admins.length } },
            { items: familyCodes, value: selectedFamilyCode, onChange: handleFamilyCodeChange, label: 'Families', id: 'ddlAllFamilyCodes', index: 3, options: { count: familyCodes.length } },
            { items: logins, value: selectedLogin, onChange: handleLoginChange, label: 'Children', id: 'ddlAllLogins', index: 4 },
            { items: teachers, value: selectedTeacher, onChange: (e: { target: { value: React.SetStateAction<string>; }; }) => setSelectedTeacher(e.target.value), label: 'Teachers', id: 'ddlTeachers', index: 5 },
            { items: students, value: selectedStudent, onChange: (e: { target: { value: React.SetStateAction<string>; }; }) => setSelectedStudent(e.target.value), label: 'Students', id: 'ddlStudents', index: 6, options: { count: students.length } },
            { items: mdReports, value: selectedMDReport, onChange: (e: { target: { value: React.SetStateAction<string>; }; }) => setSelectedMDReport(e.target.value), label: 'MD ReportNo', id: 'ddlMDreports', index: 7 },
          ].map((dropdown) =>
            renderDropdown(
              dropdown.items,
              dropdown.value,
              dropdown.onChange,
              dropdown.label,
              dropdown.id,
              dropdown.index,
              dropdown.options
            )
          )}
        </div>

        <div id="Details">
          <div id="MDreports">
            <span style={{ fontSize: 'large', color: 'white', fontWeight: 'bold' }}>NEW MD PDF ReportLinks:</span>
            <div>
              {mdReports.length === 0 ? (
                <span style={{ color: 'white' }}></span>
              ) : (
                <ul style={{ margin: '8px 0 0', paddingLeft: '18px', listStyle: 'auto' }}>
                  {mdReports.map((report) => (
                    <li key={report.value}>
                      <a
                        href={`/reports/pdf-report?reportno=${encodeURIComponent(report.value)}&schoolcode=${encodeURIComponent(selectedSchoolCode)}&familycode=${encodeURIComponent(selectedFamilyCode)}&login=${encodeURIComponent(selectedLogin)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'white', textDecorationLine: 'none' }}
                      >
                        <span>{report.label === "" ? "TEST COMPLETED DATE EMPTY" : report.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div style={{ fontSize: '14px' }}>
            <span>Details of: <strong>{selectedSchoolCode}</strong></span>
          </div>
          <div style={{ fontSize: '14px' }}><span>Name: {schoolDetails?.name}</span></div>
          <div style={{ fontSize: '14px' }}><span>Status: {schoolDetails?.status}</span></div>
          <div style={{ fontSize: '14px' }}><span>Max Users / Category: {schoolDetails?.maxusers} / {schoolDetails?.category}</span></div>
        </div>
        <br />

        <div id="divFamilyDetails" style={{ fontSize: '14px' }}>
          <div style={{ fontSize: '14px' }}><span>Details of: <strong>{selectedFamilyCode}</strong></span></div>
          <div style={{ fontSize: '14px' }}><span>Name: {familyDetails?.name}</span></div>
          <div style={{ fontSize: '14px' }}><span>Category: {getUserCategory()}</span></div>
          <div style={{ fontSize: '14px' }}><span>ExpiryDate: {familyDetails?.validity}</span></div>
          <div style={{ fontSize: '14px' }}><span>Status: {familyDetails?.status}</span></div>
          <div><span>MDs Available: {familyDetails?.mdsAvail}</span></div>
        </div>
        <br />
        <div id="divLoginDetails" style={{ fontSize: '14px' }}>
          <div style={{ fontSize: '14px' }}><span>Details of: <strong>{selectedLogin}</strong></span></div>
          <div style={{ fontSize: '14px' }}><span>Name: {loginDetails?.name}</span></div>
          <div style={{ fontSize: '14px' }}><span>Grade: {loginDetails?.grade}</span></div>
          <div style={{ fontSize: '14px' }}><span>Points: {loginDetails?.points}</span></div>
          <div style={{ fontSize: '14px' }}><span>Incentives: {loginDetails?.incentives}</span></div>
        </div>

        <hr className="subject_inner_hr" />
        <div id="btn">
          <button id="btnEdit" type="button" onClick={handleEdit}>Edit</button>
          <button id="btnGo" type="button" onClick={handleGo}>Go</button>
          <button id="btnViewLesson" type="button" onClick={handleViewLesson}>View Lesson</button>
        </div>
      </div>
          {(showAddDNC || showSearchDNC) && (
            <div style={{
              position: 'absolute',
              backgroundColor: 'rgb(204, 204, 204)',
              left: '0px',
              top: '0px',
              width: '100%',
              height: '911px',
              zIndex: 88888,
              opacity: 0.7
            }}></div>
          )}

          {showAddDNC && (
            <div style={{
              position: 'absolute',
              border: '1px solid black',
              backgroundColor: 'azure',
              width: '600px',
              height: '300px',
              zIndex: 99999,
              overflow: 'hidden',
              backgroundPositionY: 'center',
              left: '659px',
              top: '303px'
            }}>
              <EL_AddDNCNumbers mode="ADD" onClose={() => setShowAddDNC(false)} />
            </div>
          )}

          {showSearchDNC && (
            <div style={{
              position: 'absolute',
              border: '1px solid black',
              backgroundColor: 'azure',
              width: '800px',
              height: '906px',
              zIndex: 99999,
              overflow: 'hidden',
              backgroundPositionY: 'center',
              left: '559px',
              top: '0px'
            }}>
              <EL_AddDNCNumbers mode="SEARCH" onClose={() => setShowSearchDNC(false)} />
            </div>
          )}


          {alertInfo.show && (
            <CustomAlert
              message={alertInfo.message}
              onClose={() => setAlertInfo({ ...alertInfo, show: false })}
            />
          )}
          {confirmInfo.show && (
            <CustomConfirmation
              message={confirmInfo.message}
              onConfirm={() => {
                confirmInfo.onConfirm?.();
              }}
              onClose={() => setConfirmInfo({ ...confirmInfo, show: false })}
            />
          )}
    </div>
  );
};

export default Dashboard;
